import React, { useState } from 'react';

/**
 * A component that renders a password entry form to gate access to the application.
 * It checks the entered password against an environment variable.
 * @param {object} props - The component props.
 * @param {Function} props.onSuccess - Callback function to execute on successful authentication.
 * @param {boolean} props.inline - If true, renders without full-screen wrapper (for embedding)
 */
const PasswordProtect = ({ onSuccess, inline = false }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  /**
   * Handles the form submission event.
   * Prevents default page reload, checks the password, and calls onSuccess or sets an error.
   * @param {React.FormEvent<HTMLFormElement>} event - The form submission event.
   */
  const handleSubmit = (event) => {
    event.preventDefault();

    // Check if the entered password matches the one from the .env file
    if (password === process.env.REACT_APP_ACCESS_PASSWORD) {
      setError('');
      onSuccess();
    } else {
      setError('Incorrect password. Please try again.');
      setPassword(''); // Clear the input field after a failed attempt
    }
  };

  // Inline version for embedding in other layouts
  if (inline) {
    return (
      <div className="space-y-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="password"
              className="block mb-2 text-sm font-medium text-slate-300 sr-only"
            >
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-md text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition dark:bg-slate-700 dark:border-slate-600 dark:placeholder-slate-500"
              autoFocus
            />
          </div>

          <button
            type="submit"
            className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 rounded-md font-semibold text-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-blue-500 dark:bg-pink-600 dark:hover:bg-pink-700 dark:focus:ring-offset-slate-800"
          >
            Unlock
          </button>
        </form>

        {error && (
          <p className="text-red-500 dark:text-red-400 text-sm mt-2 dark:text-center dark:text-red-400">
            {error}
          </p>
        )}
      </div>
    );
  }

  // Full-screen version (original)
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-white p-4">
      <div className="w-full max-w-sm p-8 space-y-6 bg-white dark:bg-slate-900 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700">
        <h2 className="text-2xl font-bold text-center mb-6 text-slate-800 dark:text-white">Access Required</h2>
        <p className="text-center text-slate-600 dark:text-slate-300">
          Please enter the password to continue.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label
              htmlFor="password"
              className="block mb-2 text-sm font-medium text-slate-300 sr-only"
            >
              Password
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="w-full px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-md text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition dark:bg-slate-700 dark:border-slate-600 dark:placeholder-slate-500"
              autoFocus
            />
          </div>

          <button
            type="submit"
            className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 rounded-md font-semibold text-white transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white focus:ring-blue-500 dark:bg-pink-600 dark:hover:bg-pink-700 dark:focus:ring-offset-slate-800"
          >
            Unlock
          </button>
        </form>

        {error && (
          <p className="mt-4 text-center text-red-500 dark:text-red-400 text-sm dark:text-red-400">
            {error}
          </p>
        )}
      </div>
    </div>
  );
};

export default PasswordProtect;